/**
 * Public landing-page capability catalogue.
 *
 * Single source of truth for the marketing module grid + pricing add-ons shown
 * in {@link AuthLayout}. Modules are grouped into workflow categories (not one
 * card per database table) so the public surface reads as business capabilities.
 *
 * Pakistan-only capabilities (e.g. FBR Digital Invoice) carry `pakistanOnly` and
 * are filtered out for visitors outside Pakistan via {@link visibleCategories} /
 * {@link visibleAddons}.
 */
import {
  Shield, Globe, Package, Calculator, CreditCard, MapPin, FileText,
  Zap, Star, Gift, Clock, UtensilsCrossed, RotateCcw, Barcode, Layers,
  DollarSign, Landmark, Truck, Users, BarChart3, Tag, WifiOff, Calendar,
  Car, type LucideIcon,
} from 'lucide-react';

export interface ModuleCard {
  /** Stable key for React lists. */
  key: string;
  icon: LucideIcon;
  /** Tailwind gradient stops, e.g. `from-warning to-warning/80`. */
  color: string;
  label: string;
  desc: string;
  /** When true the capability is only shown to visitors in Pakistan. */
  pakistanOnly?: boolean;
}

export interface CapabilityCategory {
  id: string;
  label: string;
  items: ModuleCard[];
}

export interface PricingAddon {
  /** Backend ADDON_CATALOG key — enforced by _audit/catalog-public-drift.mjs (T-449). */
  key: string;
  label: string;
  price: string;
  pakistanOnly?: boolean;
  /** T-448 — third-party usage is billed by the provider; mirrors backend isPassThrough (drift-guarded). */
  passThrough?: boolean;
}

/** All marketing modules grouped into workflow categories. */
export const CAPABILITY_CATEGORIES: CapabilityCategory[] = [
  {
    id: 'sales-pos',
    label: 'Sales & Point of Sale',
    items: [
      { key: 'pos', icon: Zap, color: 'from-warning to-warning/80', label: 'POS + Offline Mode', desc: 'Lightning-fast checkout with barcode scan, split payments, and offline mode — works without internet.' },
      { key: 'multi-currency', icon: Globe, color: 'from-success to-success/80', label: 'Multi-Currency Payments', desc: 'Accept PKR, USD, AED, GBP, EUR, SAR at POS with live exchange rate conversion.' },
      { key: 'price-tiers', icon: Layers, color: 'from-info to-info/80', label: 'Price Tiers / Wholesale', desc: 'Different prices for Retail, Wholesale, VIP, Trade — auto-applied when customer is attached.' },
      { key: 'shifts', icon: Clock, color: 'from-success to-success/80', label: 'Cashier Shifts', desc: 'Opening float, cash in/out events, closing reconciliation — discrepancies auto-flagged.' },
      { key: 'returns', icon: RotateCcw, color: 'from-warning to-warning/80', label: 'Returns & Refunds', desc: 'Item-level partial returns, cash/store-credit refund, automatic stock restore and GL reversal.' },
      { key: 'promotions', icon: Tag, color: 'from-primary to-primary/80', label: 'Promotions & Discounts', desc: 'Create percentage or fixed discount rules with validity dates and minimum order conditions.' },
      { key: 'offline', icon: WifiOff, color: 'from-muted-foreground to-muted-foreground/80', label: 'Offline POS Mode', desc: 'Continue selling during internet outages. Sales sync automatically when connection resumes.' },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory & Purchasing',
    items: [
      { key: 'inventory', icon: Package, color: 'from-info to-info/80', label: 'Inventory & Purchases', desc: 'Smart stock management — low-stock alerts, FEFO expiry, serial/IMEI tracking, purchase orders.' },
      { key: 'locations', icon: MapPin, color: 'from-warning to-warning/80', label: 'Multi-Location & Transfers', desc: 'Unlimited branches and warehouses. Inter-location stock transfers tracked in real time.' },
      { key: 'serial', icon: Barcode, color: 'from-primary to-primary/80', label: 'Serial / IMEI Tracking', desc: 'Assign serial numbers at purchase receive; verify at POS sale — full chain-of-custody history.' },
      { key: 'expiry', icon: Calendar, color: 'from-destructive to-destructive/80', label: 'Expiry / Batch Tracking', desc: 'FEFO rotation, near-expiry alerts on dashboard, batch-level stock writing off.' },
    ],
  },
  {
    id: 'restaurant',
    label: 'Restaurant & Delivery',
    items: [
      { key: 'restaurant', icon: UtensilsCrossed, color: 'from-warning to-warning/80', label: 'Restaurant Management', desc: 'Table floor plan, KDS kitchen display, dine-in / takeaway / delivery orders, and reservations.' },
      { key: 'delivery', icon: Truck, color: 'from-destructive to-destructive/80', label: 'Delivery Order Tracking', desc: 'Create delivery orders at POS, assign riders, track dispatch → delivered status with SMS alerts.' },
      { key: 'car-takeaway', icon: Car, color: 'from-primary to-primary/80', label: 'Car Takeaway Management', desc: 'Curbside takeaway add-on for Restaurant Management: capture the customer vehicle, flag car arrival, assign a runner, and track ready → arrived → handed-over timing.' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance & Accounting',
    items: [
      { key: 'accounting', icon: Calculator, color: 'from-primary to-primary/80', label: 'Double-Entry Accounting', desc: 'Full chart of accounts, P&L, balance sheet, trial balance — journal entries posted automatically.' },
      { key: 'reports', icon: BarChart3, color: 'from-primary to-primary/80', label: 'Advanced Reports', desc: 'Sales, inventory, FBR, and expense reports with date-range filters, PDF export, and drill-down.' },
      { key: 'bank', icon: Landmark, color: 'from-muted-foreground to-muted-foreground/80', label: 'Bank & Vendor Payments', desc: 'Bank accounts, vendor payments, post-dated cheques, and full bank statement reconciliation.' },
      { key: 'expenses', icon: DollarSign, color: 'from-success to-success/80', label: 'Expense Tracking', desc: 'Categorised expense records with automatic GL entries — flows into P&L reports.' },
      { key: 'cheques', icon: CreditCard, color: 'from-destructive to-destructive/80', label: 'Cheque Management', desc: 'Post-dated cheques, clearance tracking, bounced cheque reversal — all with GL integration.' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers & Loyalty',
    items: [
      { key: 'loyalty', icon: Star, color: 'from-warning to-warning/80', label: 'Loyalty Program', desc: 'Points-per-rupee ratio, redemption at POS, balance visible on customer portal and receipt.' },
      { key: 'gift-cards', icon: Gift, color: 'from-primary to-primary/80', label: 'Gift Cards & Store Credit', desc: 'Issue gift cards, redeem at POS, check balances. Store credit for returns and loyalty rewards.' },
      { key: 'khata', icon: Users, color: 'from-info to-info/80', label: 'Customer Khata Ledger', desc: 'Sell on credit, record payments, print Khata statements, set credit limits per customer.' },
      { key: 'portal', icon: Globe, color: 'from-primary to-primary/80', label: 'Customer Self-Service Portal', desc: 'Customers view invoices, Khata balance, and loyalty points at your-store.clearring.com.' },
    ],
  },
  {
    id: 'platform',
    label: 'Platform & Compliance',
    items: [
      { key: 'fbr', icon: Shield, color: 'from-success to-success/80', label: 'FBR Digital Invoice', desc: 'All 28 FBR DI scenarios (SN001–SN028) in STAGING & LIVE. Real-time submission, auto-retry on failure.', pakistanOnly: true },
      { key: 'audit', icon: FileText, color: 'from-warning to-warning/80', label: 'Audit Log', desc: 'Full record of who created, edited, or deleted every transaction across your entire store.' },
    ],
  },
];

/** Pricing add-ons (monthly, PKR). */
export const PRICING_ADDONS: PricingAddon[] = [
  { key: 'fbr_di', label: 'FBR Digital Invoice', price: '1,000', pakistanOnly: true, passThrough: true },
  { key: 'inventory_purchases', label: 'Inventory & Purchases', price: '800' },
  { key: 'accounting_reports', label: 'Accounting & Reports', price: '1,500' },
  { key: 'extra_user', label: 'Extra Users', price: '500/user' },
  { key: 'extra_location', label: 'Extra Location', price: '1,000/loc' },
  { key: 'restaurant_management', label: 'Restaurant Module', price: '1,500' },
  { key: 'car_takeaway', label: 'Car Takeaway Management', price: '800' },
  { key: 'expense_tracking', label: 'Expense Tracking', price: '500' },
  { key: 'bank_reconciliation', label: 'Bank Reconciliation', price: '1,000' },
  { key: 'tax_management', label: 'Tax Management', price: '1,000' },
  { key: 'stock_transfers', label: 'Stock Transfers', price: '1,000' },
  { key: 'import_templates', label: 'Import Templates', price: '1,500' },
  { key: 'api_access', label: 'API Access', price: '1,500' },
  { key: 'sms_alerts', label: 'SMS Alerts', price: '500', passThrough: true },
  { key: 'promotion_code', label: 'Promotion Codes', price: '1,000/promo' },
  { key: 'mobile_links', label: 'Mobile Links (1 included)', price: '500/pack' },
];

/** Categories with Pakistan-only modules filtered out for non-PK visitors. */
export function visibleCategories(isPakistan: boolean): CapabilityCategory[] {
  if (isPakistan) return CAPABILITY_CATEGORIES;
  return CAPABILITY_CATEGORIES
    .map((c) => ({ ...c, items: c.items.filter((m) => !m.pakistanOnly) }))
    .filter((c) => c.items.length > 0);
}

/** Add-ons with Pakistan-only entries filtered out for non-PK visitors. */
export function visibleAddons(isPakistan: boolean): PricingAddon[] {
  return isPakistan ? PRICING_ADDONS : PRICING_ADDONS.filter((a) => !a.pakistanOnly);
}

/** Total count of capabilities visible to the given audience. */
export function visibleModuleCount(isPakistan: boolean): number {
  return visibleCategories(isPakistan).reduce((n, c) => n + c.items.length, 0);
}
